import { formatFollowedSubCategory, returnPaginatedResponse } from "../format";
import useRequest from "~/hooks/use-request";
import useUserStore from "~/stores/user";
import HeaderAPI from "~/services/header";

const FollowedCategoriesAPI = {
  load(options: IRequestPaginatedParams & { version: number }): Promise<IModelFollowedSubCategoryItem[]> {
    const { request } = useRequest();
    const userStore = useUserStore();

    return request
      .get<IPaginatedResponse<IResponseFollowedCategoryItem>>("/api/follow/list_by_user", { query: options, setAuthorization: true })
      .then((response) => {
        const followedItems = response.content.entries.map(formatFollowedSubCategory);
        followedItems.forEach(item => (userStore.clubs[item.id] = item));
        return followedItems;
      });
  },

  loadCache(options: IRequestPaginatedOptions & { mainCategoryId: string }) {
    const userStore = useUserStore();
    const followedSubCategories = Object.values(userStore.clubs);

    if (options.mainCategoryId) {
      return Promise.resolve(
        returnPaginatedResponse(
          followedSubCategories.filter(subCategory => subCategory.categoryIds.includes(options.mainCategoryId)),
        ),
      );
    }

    return Promise.resolve(returnPaginatedResponse(followedSubCategories));
  },

  async loadCacheGroupedByCategory() {
    const appConfig = useRuntimeConfig().public;
    const userStore = useUserStore();
    const cachedCategory = useSessionStorage<IHeaderLink[]>(`${appConfig.APP_CUSTOMER.name_short}-links`, []);
    const followedMainCategoryIds = userStore.followedMainCategoryIds;
    const followedIdentities = Object.values(userStore.clubs);

    if (!(cachedCategory.value && cachedCategory.value.length))
      await HeaderAPI.load(); // Check if category is already exists on sessios storage

    const group = followedMainCategoryIds.reduce((acc: IModelGroupedFollowedCategoryItems[], curr: string) => {
      const title = cachedCategory.value.find(c => c.id === curr)?.title;
      if (!title)
        return acc;
      const item = { id: curr, title, followedIdentities: followedIdentities.filter(s => s.categoryIds.includes(curr)) };
      return [...acc, item];
    }, []);

    return group;
  },

  follow(item: IModelSubCategory, type: "identity" | "identity_item"): Promise<IPaginatedResponse<IResponseFollowedCategoryItem>> {
    const { request } = useRequest();
    const params = { reference_id: item.id, type };

    return request
      .post<IPaginatedResponse<IResponseFollowedCategoryItem>>("/api/follow/save", {
        body: params,
        setAuthorization: true,
      })
      .then((response) => {
        // TODO: Temporary decision until BE fix error with following. It fix displaying of identity items if user has more than 20 followed identity items.
        FollowedCategoriesAPI.load({ count: true, size: 150, version: 2 });

        return response;
      });
  },

  unFollow(item: IModelSubCategory): Promise<void> {
    const { request } = useRequest();
    const userStore = useUserStore();
    const ref = userStore.clubs[item.id].referenceId;
    const params = { reference_id: ref };

    return request.post<null>("/api/follow/remove", { body: params, setAuthorization: true }).then(() => {
      delete userStore.clubs[item.id];
    });
  },
};

export default FollowedCategoriesAPI;
